import type { RefObject } from "react";
import type { StepFailure } from "../lib/stepTestFailures";
import type { ChatHandle } from "../components/Chat/chatTypes";

export function useInvestigateStepTest(chatRef: RefObject<ChatHandle | null>) {
  const investigateFailures = (failures: readonly StepFailure[]) => {
    const context = failures
      .map(({ stepDir, specs, timedOut }) => {
        if (timedOut) return `- “${stepDir}”: the step tests timed out.`;
        if (specs.length === 0)
          return `- “${stepDir}”: the step tests failed before reporting individual test results.`;
        return `- “${stepDir}”: ${specs
          .map(
            ({ name, message }) => `“${name}”${message ? ` (${message})` : ""}`
          )
          .join("; ")}`;
      })
      .join("\n");
    chatRef.current?.enqueueOrSend({
      text: `Fix all of these step test failures:
${context}

Run each affected step’s tests with runStepTests to reproduce the failures and note which tests pass. Read each step’s code, execution logs, and the failing tests’ recorded inputs and expected outputs to find the root causes. For timed-out tests, check whether the original executions have since completed before starting another run.

Fix the root causes in the affected steps’ code without asking me to approve the fixes, then re-run all tests for every affected step to verify they pass. Preserve each step’s intended behavior and keep its currently passing tests passing. Work through the whole list, and summarize what you changed and the test results.

Don’t edit or regenerate step test files to make them pass. If a test is obsolete or nondeterministic, or making it pass would break the step’s other currently passing tests, explain why and use askQuestion to ask whether to remove it with unpinStepTest. Otherwise, ask a follow-up question only if the intended behavior is ambiguous or you need information you cannot obtain yourself.`,
      actionKind: "investigate_step_test",
      selectedStep: failures.length === 1 ? failures[0]?.stepDir : undefined,
    });
  };
  return {
    investigateFailures,
    investigateFailure: ({
      stepDir,
      testName,
    }: {
      stepDir: string;
      testName: string;
    }) =>
      investigateFailures([
        {
          stepDir,
          timedOut: false,
          specs: testName ? [{ name: testName, message: null }] : [],
        },
      ]),
  };
}
